import React from 'react';
import { useParams, Redirect, Link } from 'react-router-dom';
import { FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import caseStudies from '../data/caseStudies';

const CaseStudyDetail = () => {
    const { slug } = useParams();
    const study = caseStudies.find((s) => s.slug === slug);

    if (!study || !study.published) {
        return <Redirect to="/error" />;
    }

    const {
        title,
        client,
        industry,
        atAGlance,
        challenge,
        whatWeBuilt,
        howItWorks,
        technologies,
        outcome,
        quote,
        relatedProducts,
        relatedServices,
        relatedCaseStudies,
        whatWeBuiltLabel,
        howItWorksLabel,
    } = study;

    return (
        <>
            <SEO title={`${title} — Outlier Engineering Group`} />
            <Layout>

                {/* Breadcrumb */}
                <div style={{ paddingTop: '32px', paddingBottom: '0' }}>
                    <div className="container">
                        <nav style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'rgba(255,255,255,0.35)' }}>
                            <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
                            <span>›</span>
                            <Link to="/case-studies" style={{ color: 'inherit', textDecoration: 'none' }}>Case Studies</Link>
                            <span>›</span>
                            <span style={{ color: 'rgba(255,255,255,0.6)' }}>{title}</span>
                        </nav>
                    </div>
                </div>

                {/* Hero */}
                <div style={{ padding: '52px 0 56px' }}>
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-8">
                                <span style={{
                                    display: 'inline-block',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                    color: 'var(--color-primary)',
                                    marginBottom: '18px',
                                }}>
                                    Case study
                                </span>
                                <h1 style={{
                                    fontSize: 'clamp(2rem, 4vw, 3rem)',
                                    fontWeight: 800,
                                    lineHeight: 1.15,
                                    color: 'var(--color-heading)',
                                    margin: '0 0 20px 0',
                                }}>
                                    {title}
                                </h1>
                                <p style={{
                                    fontSize: '17px',
                                    color: 'var(--color-body)',
                                    margin: 0,
                                }}>
                                    {[client, industry].filter(Boolean).join(' · ')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div style={{ borderTop: '1px solid var(--color-border)' }} />

                {/* Main content + sidebar */}
                <div style={{ padding: '64px 0 80px' }}>
                    <div className="container">
                        <div className="row">

                            {/* Sidebar — At a glance (appears first on mobile, right on desktop) */}
                            {atAGlance && atAGlance.length > 0 && (
                                <div className="col-lg-4 order-1 order-lg-2" style={{ marginBottom: '48px' }}>
                                    <div style={{
                                        background: 'var(--color-extra04)',
                                        border: '1px solid var(--color-border)',
                                        borderTop: '2px solid var(--color-primary)',
                                        borderRadius: 'var(--radius)',
                                        padding: '28px',
                                        position: 'sticky',
                                        top: '90px',
                                    }}>
                                        <p style={{
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            letterSpacing: '0.12em',
                                            textTransform: 'uppercase',
                                            color: 'rgba(255,255,255,0.35)',
                                            margin: '0 0 20px 0',
                                        }}>
                                            At a glance
                                        </p>
                                        {atAGlance.map((item, i) => (
                                            <div key={i} style={{
                                                marginBottom: i < atAGlance.length - 1 ? '18px' : 0,
                                                paddingBottom: i < atAGlance.length - 1 ? '18px' : 0,
                                                borderBottom: i < atAGlance.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                                            }}>
                                                <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', margin: '0 0 5px 0' }}>
                                                    {item.label}
                                                </p>
                                                <p style={{ fontSize: '15px', fontWeight: 500, color: 'rgba(255,255,255,0.85)', margin: 0, lineHeight: 1.4 }}>
                                                    {item.value}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Main content */}
                            <div className="col-lg-7 order-2 order-lg-1" style={{ paddingRight: '40px' }}>

                                {/* The challenge */}
                                {challenge && challenge.length > 0 && (
                                    <section style={{ marginBottom: '56px' }}>
                                        <h2 style={{
                                            fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                                            fontWeight: 700,
                                            color: 'var(--color-heading)',
                                            margin: '0 0 24px 0',
                                            paddingBottom: '16px',
                                            borderBottom: '1px solid rgba(255,255,255,0.07)',
                                        }}>
                                            The challenge
                                        </h2>
                                        {challenge.map((para, i) => (
                                            <p key={i} style={{
                                                fontSize: '17px',
                                                lineHeight: 1.8,
                                                color: 'var(--color-body)',
                                                margin: i < challenge.length - 1 ? '0 0 18px 0' : 0,
                                            }}>
                                                {para}
                                            </p>
                                        ))}
                                    </section>
                                )}

                                {/* What we built */}
                                {whatWeBuilt && whatWeBuilt.length > 0 && (
                                    <section style={{ marginBottom: '56px' }}>
                                        <h2 style={{
                                            fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                                            fontWeight: 700,
                                            color: 'var(--color-heading)',
                                            margin: '0 0 24px 0',
                                            paddingBottom: '16px',
                                            borderBottom: '1px solid rgba(255,255,255,0.07)',
                                        }}>
                                            {whatWeBuiltLabel || 'What we built'}
                                        </h2>
                                        {whatWeBuilt.map((para, i) => (
                                            <p key={i} style={{
                                                fontSize: '17px',
                                                lineHeight: 1.8,
                                                color: 'var(--color-body)',
                                                margin: i < whatWeBuilt.length - 1 ? '0 0 18px 0' : 0,
                                            }}>
                                                {para}
                                            </p>
                                        ))}
                                    </section>
                                )}

                                {/* How it works */}
                                {howItWorks && howItWorks.length > 0 && (
                                    <section style={{ marginBottom: '56px' }}>
                                        <h2 style={{
                                            fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                                            fontWeight: 700,
                                            color: 'var(--color-heading)',
                                            margin: '0 0 24px 0',
                                            paddingBottom: '16px',
                                            borderBottom: '1px solid rgba(255,255,255,0.07)',
                                        }}>
                                            {howItWorksLabel || 'How it works'}
                                        </h2>
                                        <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                                            {howItWorks.map((step, i) => (
                                                <li key={i} style={{
                                                    display: 'flex',
                                                    gap: '16px',
                                                    marginBottom: i < howItWorks.length - 1 ? '16px' : 0,
                                                }}>
                                                    <span style={{
                                                        flexShrink: 0,
                                                        width: '28px',
                                                        height: '28px',
                                                        borderRadius: '50%',
                                                        background: 'rgba(255, 90, 9, 0.12)',
                                                        border: '1px solid rgba(255, 90, 9, 0.3)',
                                                        color: 'var(--color-primary)',
                                                        fontSize: '12px',
                                                        fontWeight: 700,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        marginTop: '2px',
                                                    }}>
                                                        {i + 1}
                                                    </span>
                                                    <p style={{
                                                        fontSize: '16px',
                                                        lineHeight: 1.7,
                                                        color: 'var(--color-body)',
                                                        margin: 0,
                                                    }}>
                                                        {step}
                                                    </p>
                                                </li>
                                            ))}
                                        </ol>
                                    </section>
                                )}

                                {/* Technologies */}
                                {technologies && technologies.length > 0 && (
                                    <section style={{ marginBottom: '56px' }}>
                                        <h2 style={{
                                            fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                                            fontWeight: 700,
                                            color: 'var(--color-heading)',
                                            margin: '0 0 24px 0',
                                            paddingBottom: '16px',
                                            borderBottom: '1px solid rgba(255,255,255,0.07)',
                                        }}>
                                            Technologies and protocols
                                        </h2>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                            {technologies.map((tech, i) => (
                                                <span key={i} style={{
                                                    fontSize: '13px',
                                                    fontWeight: 500,
                                                    padding: '7px 14px',
                                                    borderRadius: '6px',
                                                    background: 'rgba(255, 90, 9, 0.08)',
                                                    border: '1px solid rgba(255, 90, 9, 0.22)',
                                                    color: 'rgba(255,255,255,0.75)',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {tech}
                                                </span>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {/* Outcome */}
                                {outcome && (
                                    <section style={{ marginBottom: '56px' }}>
                                        <h2 style={{
                                            fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                                            fontWeight: 700,
                                            color: 'var(--color-heading)',
                                            margin: '0 0 24px 0',
                                            paddingBottom: '16px',
                                            borderBottom: '1px solid rgba(255,255,255,0.07)',
                                        }}>
                                            Outcome
                                        </h2>
                                        <p style={{
                                            fontSize: '17px',
                                            lineHeight: 1.8,
                                            color: 'var(--color-body)',
                                            margin: 0,
                                        }}>
                                            {outcome}
                                        </p>
                                    </section>
                                )}

                                {/* Quote */}
                                {quote && (
                                    <section style={{ marginBottom: '56px' }}>
                                        <blockquote style={{
                                            margin: 0,
                                            padding: '24px 28px',
                                            background: 'var(--color-extra04)',
                                            borderLeft: '3px solid var(--color-primary)',
                                            borderRadius: '0 var(--radius) var(--radius) 0',
                                        }}>
                                            <p style={{
                                                fontSize: '18px',
                                                lineHeight: 1.7,
                                                fontStyle: 'italic',
                                                color: 'rgba(255,255,255,0.8)',
                                                margin: '0 0 12px 0',
                                            }}>
                                                "{quote.text}"
                                            </p>
                                            {quote.attribution && (
                                                <footer style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', fontStyle: 'normal' }}>
                                                    — {quote.attribution}
                                                </footer>
                                            )}
                                        </blockquote>
                                    </section>
                                )}

                                {/* Related */}
                                {((relatedProducts && relatedProducts.length > 0) || (relatedServices && relatedServices.length > 0) || (relatedCaseStudies && relatedCaseStudies.length > 0)) && (
                                    <section>
                                        <h2 style={{
                                            fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                                            fontWeight: 700,
                                            color: 'var(--color-heading)',
                                            margin: '0 0 24px 0',
                                            paddingBottom: '16px',
                                            borderBottom: '1px solid rgba(255,255,255,0.07)',
                                        }}>
                                            Related
                                        </h2>
                                        {relatedServices && relatedServices.length > 0 && (
                                            <div style={{ marginBottom: relatedProducts && relatedProducts.length > 0 ? '14px' : 0 }}>
                                                <span style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginRight: '10px' }}>Services</span>
                                                <span style={{ fontSize: '15px', color: 'var(--color-body)' }}>{relatedServices.join(' · ')}</span>
                                            </div>
                                        )}
                                        {relatedProducts && relatedProducts.length > 0 && (
                                            <div style={{ marginBottom: relatedCaseStudies && relatedCaseStudies.length > 0 ? '14px' : 0 }}>
                                                <span style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginRight: '10px' }}>Products</span>
                                                {relatedProducts.map((p, i) => (
                                                    <React.Fragment key={i}>
                                                        {p === 'Wayside Watcher Platform'
                                                            ? <Link to="/wayside-watcher" style={{ fontSize: '15px', color: 'var(--color-primary)' }}>{p}</Link>
                                                            : <span style={{ fontSize: '15px', color: 'var(--color-body)' }}>{p}</span>
                                                        }
                                                        {i < relatedProducts.length - 1 && <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 6px' }}>·</span>}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        )}
                                        {relatedCaseStudies && relatedCaseStudies.length > 0 && (
                                            <div>
                                                <span style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginRight: '10px' }}>Related case studies</span>
                                                {relatedCaseStudies.map((cs, i) => {
                                                    const linked = caseStudies.find(s => s.slug === cs);
                                                    return linked ? (
                                                        <React.Fragment key={i}>
                                                            <Link to={`/case-studies/${linked.slug}`} style={{ fontSize: '15px', color: 'var(--color-primary)' }}>{linked.title}</Link>
                                                            {i < relatedCaseStudies.length - 1 && <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 6px' }}>·</span>}
                                                        </React.Fragment>
                                                    ) : null;
                                                })}
                                            </div>
                                        )}
                                    </section>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div style={{ borderTop: '1px solid var(--color-border)' }} />

                {/* Contextual CTA */}
                <div style={{ padding: '80px 0', textAlign: 'center' }}>
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-6">
                                <h2 style={{
                                    fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
                                    fontWeight: 700,
                                    color: 'var(--color-heading)',
                                    margin: '0 0 14px 0',
                                }}>
                                    Working on a similar project?
                                </h2>
                                <p style={{ fontSize: '16px', color: 'var(--color-body)', marginBottom: '36px' }}>
                                    Tell us what you're building.
                                </p>
                                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Link
                                        className="btn-default btn-icon"
                                        to={`/contact?subject=case_study_${slug}`}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                    >
                                        Get in touch <FiArrowRight />
                                    </Link>
                                    <Link
                                        to="/case-studies"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontSize: '15px',
                                            color: 'rgba(255,255,255,0.5)',
                                            textDecoration: 'none',
                                            padding: '10px 0',
                                        }}
                                    >
                                        <FiArrowLeft size={14} /> All case studies
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </Layout>
        </>
    );
};

export default CaseStudyDetail;
